import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import FundIcon from 'assets/images/funds/moneypool-2.png'
import Fund2Icon from 'assets/images/funds/moneyPool-1.svg'
import { ConvertJalaliDate } from 'utilities/helper/digitToMonth'
interface ISubHeaderProps {
    capital: number
    workingCapital: number
    intervalPotency: number
    date: string
    intervalShareIncome: number
    totalShares: number
    shareValueAmount: number
    cashinsAmount: number
    cashoutAmounts: number
    createDate: string
    imageId: number
    avatar: string | null
    name: string
    isFuckingManager: () => boolean
    showBalance: boolean
    hiddenFinanceDetail: boolean
    remainingDays: number | null
}
export default function SubHeader({
    capital,
    workingCapital,
    intervalPotency,
    date,
    intervalShareIncome,
    totalShares,
    shareValueAmount,
    cashinsAmount,
    cashoutAmounts,
    createDate,
    imageId,
    avatar,
    name,
    isFuckingManager,
    showBalance,
    hiddenFinanceDetail,
    remainingDays,
}: ISubHeaderProps) {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    // A fund whose uploaded image is gone from storage (some were lost in the
    // bucket migration) rendered as a broken-image glyph. Remember the src
    // that failed rather than a bare boolean, so navigating to another fund
    // with a working avatar still shows it.
    const [failedAvatar, setFailedAvatar] = useState<string | null>(null)
    const showCustomAvatar = Boolean(avatar) && avatar !== failedAvatar
    const { state } = useLocation()
    const isNormal = !isFuckingManager()
    const balanceHidden = isNormal && !showBalance
    const financeHidden = isNormal && hiddenFinanceDetail
    const toman = (v: number) =>
        balanceHidden ? '-' : `${v ? v.toLocaleString() : 0} تومان`

    return (
        <div className="bg-white w-full">
            {/* Fund name — «صندوق %s», centered, 16sp, marginTop 6dp, single line */}
            <p className="text-center text-base text-black mt-1.5 px-[22px] truncate">
                صندوق {name}
            </p>

            {/* Info block: avatar on the RIGHT (10dp edge), the two packed
                columns (labels then values, 16dp apart) hug the avatar —
                free space stays on the far LEFT like Android */}
            <div className="flex items-center gap-2 mt-[3px] mb-1 pr-2.5 pl-2">
                {showCustomAvatar ? (
                    <img
                        src={avatar as string}
                        alt="fund"
                        className="w-20 h-20 shrink-0 rounded-full object-cover"
                        onError={() => setFailedAvatar(avatar)}
                    />
                ) : (
                    <img
                        src={imageId === 2 ? FundIcon : Fund2Icon}
                        alt="fund"
                        className="w-20 h-20 shrink-0 object-contain"
                    />
                )}
                <div className="flex-1 min-w-0 min-h-[70px] flex items-center">
                    <div className="grid grid-cols-[auto_auto] gap-x-4 text-sm">
                        <span className="text-text-muted">سرمایه صندوق</span>
                        <span className="text-black text-right">
                            {toman(capital)}
                        </span>
                        {!financeHidden && (
                            <>
                                <span className="text-text-muted">
                                    سرمایه در گردش
                                </span>
                                <span className="text-black text-right">
                                    {toman(workingCapital)}
                                </span>
                                <span className="text-text-muted">
                                    توان دوره
                                </span>
                                <span className="text-black text-right">
                                    {intervalPotency
                                        ? intervalPotency.toLocaleString()
                                        : 0}{' '}
                                    تومان
                                </span>
                            </>
                        )}
                        <span className="text-text-muted">تاریخ سررسید</span>
                        <span className="text-black text-right">
                            {ConvertJalaliDate(date)}
                        </span>
                    </div>
                </div>
            </div>

            {/* More-info band: #EFEFEF, 1px gray stroke, 4px radius, content
                packed & centered; collapses/expands with 300ms animation */}
            <div
                className={`mx-4 overflow-hidden transition-[max-height] duration-300 ${
                    isOpen ? 'max-h-[300px]' : 'max-h-0'
                }`}
            >
                <div className="rounded-[4px] border border-[#D4D4D4] bg-[#EFEFEF] px-4 pt-0.5 pb-1 flex justify-center">
                    <div className="grid grid-cols-[auto_auto] gap-x-4 text-sm">
                        <span className="text-text-muted">ورودی ماهانه</span>
                        <span className="text-black text-right">
                            {intervalShareIncome
                                ? intervalShareIncome.toLocaleString()
                                : 0}{' '}
                            تومان
                        </span>
                        {shareValueAmount !== 0 && (
                            <>
                                <span className="text-text-muted">
                                    تعداد سهم ورودی
                                </span>
                                <span className="text-black text-right">
                                    {totalShares
                                        ? totalShares.toLocaleString()
                                        : 0}{' '}
                                    سهم
                                </span>
                                <span className="text-text-muted">
                                    حق عضویت پایه
                                </span>
                                <span className="text-black text-right">
                                    {shareValueAmount.toLocaleString()} تومان
                                </span>
                            </>
                        )}
                        {!financeHidden && (
                            <>
                                <span className="text-text-muted">
                                    مجموع واریزها
                                </span>
                                <span className="text-black text-right">
                                    {toman(cashinsAmount)}
                                </span>
                                <span className="text-text-muted">
                                    مجموع برداشت‌ها
                                </span>
                                <span className="text-black text-right">
                                    {toman(cashoutAmounts)}
                                </span>
                            </>
                        )}
                        <span className="text-text-muted">
                            تاریخ ایجاد صندوق
                        </span>
                        <span className="text-black text-right">
                            {ConvertJalaliDate(createDate)}
                        </span>
                        {!!remainingDays && (
                            <>
                                <span className="text-text-muted">
                                    مدت اعتبار باقیمانده
                                </span>
                                <span className="text-black text-right">
                                    {remainingDays.toLocaleString()} روز
                                </span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* More/less toggle: blue text with chevron on its LEFT,
                bottom-rounded band attached under the info band */}
            <div className="flex justify-center mb-2.5 -mt-px">
                <button
                    className="inline-flex items-center justify-center gap-2 rounded-b-[4px] border border-[#D4D4D4] bg-[#EFEFEF] px-2 py-0.5 text-sm font-bold text-[#2196F3]"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {isOpen ? 'اطلاعات کمتر' : 'اطلاعات بیشتر'}
                    <svg
                        className={`w-4 h-4 transition-transform duration-300 ${
                            isOpen ? '' : 'rotate-180'
                        }`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <polyline points="18 15 12 9 6 15" />
                    </svg>
                </button>
            </div>
        </div>
    )
}
